import { useMemo } from "react";
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import startCase from "lodash/startCase";
import { isOhbatRazaEvent, RAZA_EVENT_KIND_BACKED_LABEL } from "@/utils/raza-event-display";
import { alphaOnSurface } from "@/theme/surfaceAlpha";
import { razaEventOhbatTypeBackground } from "@/theme/raza-event-ohbat-type-color";
import { shouldFlagMissingSadarat } from "../missing-sadarat-highlight";
import type { RazaEventsCalendarEvent } from "./types";

const RAZA_BACKED_LEGEND_KEY = "__raza_backed__";
const HALL_OVERLAY_LEGEND_KEY = "__hall_overlay__";

type TypeLegendItem = {
  key: string;
  label: string;
  typeKey: string;
};

function legendSwatchColor(typeKey: string, theme: Theme) {
  if (typeKey === RAZA_BACKED_LEGEND_KEY) {
    return theme.palette.info.dark;
  }
  if (typeKey === HALL_OVERLAY_LEGEND_KEY) {
    return theme.palette.secondary.dark;
  }
  return razaEventOhbatTypeBackground(typeKey, theme);
}

function buildTypeLegendItems(events: RazaEventsCalendarEvent[]): TypeLegendItem[] {
  const byKey = new Map<string, TypeLegendItem>();
  let hasRazaBacked = false;
  let hasHallOverlay = false;
  for (const ev of events) {
    if (ev.overlayKind === "hall") {
      hasHallOverlay = true;
      continue;
    }
    const row = ev.resource;
    if (!row) continue;
    if (!isOhbatRazaEvent(row)) {
      hasRazaBacked = true;
      continue;
    }
    const raw = String(row.ohbatType ?? "");
    const key = raw || "__other__";
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        label: raw ? startCase(raw) : "Other",
        typeKey: raw,
      });
    }
  }
  const items = [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
  if (hasRazaBacked) {
    items.push({
      key: RAZA_BACKED_LEGEND_KEY,
      label: RAZA_EVENT_KIND_BACKED_LABEL,
      typeKey: RAZA_BACKED_LEGEND_KEY,
    });
  }
  if (hasHallOverlay) {
    items.push({
      key: HALL_OVERLAY_LEGEND_KEY,
      label: "Hall booking",
      typeKey: HALL_OVERLAY_LEGEND_KEY,
    });
  }
  return items;
}

function calendarHasMissingSadarat(events: RazaEventsCalendarEvent[]): boolean {
  return events.some(
    (e) => e.overlayKind !== "hall" && e.resource && shouldFlagMissingSadarat(e.resource)
  );
}

type Props = {
  events: RazaEventsCalendarEvent[];
  hallOverlayEnabled?: boolean;
  onHallOverlayChange?: (enabled: boolean) => void;
};

export function RazaEventsCalendarLegend({
  events,
  hallOverlayEnabled = false,
  onHallOverlayChange,
}: Props) {
  const theme = useTheme();
  const typeItems = useMemo(() => buildTypeLegendItems(events), [events]);
  const showMissingSadarat = useMemo(() => calendarHasMissingSadarat(events), [events]);
  const isDark = theme.palette.mode === "dark";
  const legendScrollRow = useMediaQuery(theme.breakpoints.down("sm"));

  if (typeItems.length === 0 && !showMissingSadarat && !onHallOverlayChange) {
    return null;
  }

  const swatchBorder = {
    border: "1px solid",
    borderColor: alphaOnSurface(theme, 0.28, 0.12),
    boxShadow: isDark ? `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.06)}` : "none",
  };

  return (
    <Box
      role="region"
      aria-label="Event type legend"
      sx={{
        flexShrink: 0,
        pt: 1,
        pb: 1,
        px: 2,
        borderTop: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1.25}
        useFlexGap
        sx={{
          flexWrap: legendScrollRow ? "nowrap" : "wrap",
          rowGap: 0.75,
          ...(legendScrollRow && {
            overflowX: "auto",
            overflowY: "hidden",
            pb: 0.25,
            WebkitOverflowScrolling: "touch",
            scrollbarGutter: "stable",
          }),
        }}
      >
        {onHallOverlayChange ? (
          <FormControlLabel
            sx={{ mr: 0.5, flexShrink: 0, ml: 0 }}
            control={
              <Switch
                size="small"
                checked={hallOverlayEnabled}
                onChange={(_e, checked) => onHallOverlayChange(checked)}
                inputProps={{ "aria-label": "Show hall bookings overlay" }}
              />
            }
            label={
              <Typography variant="body2" color="text.primary" noWrap>
                Show halls
              </Typography>
            }
          />
        ) : null}
        {typeItems.map((item) => (
          <Stack
            key={item.key}
            direction="row"
            alignItems="center"
            gap={0.75}
            sx={{ flexShrink: 0, maxWidth: legendScrollRow ? "none" : "100%" }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: 0.5,
                flexShrink: 0,
                bgcolor: legendSwatchColor(item.typeKey, theme),
                ...(item.typeKey === HALL_OVERLAY_LEGEND_KEY
                  ? {
                      border: "1px dashed",
                      borderColor: "secondary.light",
                    }
                  : swatchBorder),
              }}
            />
            <Typography variant="body2" color="text.primary" noWrap title={item.label}>
              {item.label}
            </Typography>
          </Stack>
        ))}
        {showMissingSadarat && (
          <Stack
            direction="row"
            alignItems="center"
            gap={0.75}
            sx={{ flexShrink: 0, maxWidth: legendScrollRow ? "none" : "100%" }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: 0.5,
                flexShrink: 0,
                display: "flex",
                overflow: "hidden",
                border: "1px solid",
                borderColor: alphaOnSurface(theme, 0.22, 0.12),
                boxShadow: isDark
                  ? `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.06)}`
                  : "none",
              }}
            >
              <Box sx={{ width: 5, flexShrink: 0, bgcolor: "error.main" }} />
              <Box sx={{ flex: 1, minWidth: 0, bgcolor: "action.hover" }} />
            </Box>
            <Typography variant="body2" color="text.secondary" noWrap title="Missing sadarat">
              Missing sadarat (red edge)
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
